// src/components/VideoPlayer/VideoPlayer.tsx
import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Container, Typography, Box, CircularProgress, Button, Paper, Divider } from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import type { VimeoVideo } from "../../services/vimeo.service"
import vimeoService from "../../services/vimeo.service"

const VideoPlayer: React.FC = () => {
  const { videoId } = useParams<{ videoId: string }>()
  const [video, setVideo] = useState<VimeoVideo | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchVideo = async () => {
      if (!videoId) {
        setError("Video ID is missing")
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const videoData = await vimeoService.getVideo(videoId)
        setVideo(videoData)
        setLoading(false)
      } catch (err) {
        setError("Failed to load video. It might be private or no longer available.")
        setLoading(false)
        console.error("Error fetching video:", err)
      }
    }

    fetchVideo()
  }, [videoId])

  const handleBack = () => {
    navigate(-1)
  }

  // Format date to a more readable format
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    )
  }

  if (error || !video) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
          Back to Videos
        </Button>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <Typography color="error">{error || "Video not found"}</Typography>
        </Box>
      </Container>
    )
  }

  // Get the video embed URL
  const embedUrl = video.link.replace("vimeo.com/", "player.vimeo.com/video/")

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
        Back to Videos
      </Button>

      <Typography variant="h4" component="h1" gutterBottom>
        {video.name}
      </Typography>

      <Paper elevation={2} sx={{ mb: 3 }}>
        <Box sx={{ position: "relative", paddingTop: "56.25%", overflow: "hidden" }}>
          <iframe
            src={embedUrl}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: "none",
            }}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title={video.name}
          />
        </Box>
      </Paper>

      <Box mb={4}>
        <Typography variant="body1" paragraph>
          {video.description}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary">
          Uploaded on {formatDate(video.created_time)}
        </Typography>
      </Box>
    </Container>
  )
}

export default VideoPlayer
